export interface ConsoleLogEntry {
  type: 'log' | 'warn' | 'error' | 'info';
  message: string;
  timestamp: string;
  stack?: string;
}

let isInitialized = false;
let maxEntries = 100;
const logs: ConsoleLogEntry[] = [];

const createMessage = (args: any[]): { message: string; stack?: string } => {
  let stack: string | undefined;
  const message = args
    .map(arg => {
      if (arg === null) return 'null';
      if (arg === undefined) return 'undefined';
      if (typeof arg === 'string') return arg;
      if (typeof arg === 'number' || typeof arg === 'boolean') return String(arg);
      if (typeof arg === 'object') {
        try {
          if (arg instanceof Error) {
            if (!stack) {
              stack = arg.stack;
            }
            return `${arg.name}: ${arg.message}`;
          }
          return JSON.stringify(arg);
        } catch {
          return '[Object]';
        }
      }
      return String(arg);
    })
    .join(' ');

  return { message, stack };
};

export const initConsoleCapture = (entries: number = 100): void => {
  if (typeof window === 'undefined' || isInitialized) {
    return;
  }

  isInitialized = true;
  maxEntries = entries;

  const original = {
    log: console.log,
    warn: console.warn,
    error: console.error,
    info: console.info
  };

  const pushLog = (type: ConsoleLogEntry['type'], args: any[]) => {
    const { message, stack } = createMessage(args);
    const entry: ConsoleLogEntry = {
      type,
      message,
      timestamp: new Date().toISOString(),
      ...(type === 'error' && stack ? { stack } : {})
    };

    logs.push(entry);
    if (logs.length > maxEntries) {
      logs.shift();
    }
  };

  console.log = (...args: any[]) => {
    pushLog('log', args);
    original.log.apply(console, args);
  };
  console.warn = (...args: any[]) => {
    pushLog('warn', args);
    original.warn.apply(console, args);
  };
  console.error = (...args: any[]) => {
    pushLog('error', args);
    original.error.apply(console, args);
  };
  console.info = (...args: any[]) => {
    pushLog('info', args);
    original.info.apply(console, args);
  };
};

export const getCapturedConsoleLogs = (limit?: number): ConsoleLogEntry[] => {
  if (typeof limit === 'number') {
    return logs.slice(-limit);
  }
  return [...logs];
};

export const clearCapturedConsoleLogs = (): void => {
  logs.length = 0;
};
